import React from "react";
import { TypographyOptions } from "@mui/material/styles/createTypography";
import pxToRem from "../functions/pxToRem";
import colors from "./colors";

const { text, success, warning } = colors;

// ─────────────────────────────────────────────────────────────
// BASE SCALE — size + weight only, no colour
// These are the 8 real typographic decisions.
// Colour is applied separately via sx, className, or color prop.
// ─────────────────────────────────────────────────────────────
const baseProperties = {
  fontFamily: "var(--font-inter)",
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
};

// Size × weight building blocks
const Title1 = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 600,
  fontSize: pxToRem(32),
  lineHeight: pxToRem(40),
  "@media (max-width:900px)": {
    fontSize: pxToRem(28),
    lineHeight: pxToRem(36),
  },
  "@media (max-width:600px)": {
    fontSize: pxToRem(24),
    lineHeight: pxToRem(32),
  },
};
const Title2 = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 600,
  fontSize: pxToRem(28),
  lineHeight: pxToRem(36),
  "@media (max-width:900px)": {
    fontSize: pxToRem(24),
    lineHeight: pxToRem(32),
  },
  "@media (max-width:600px)": {
    fontSize: pxToRem(20),
    lineHeight: pxToRem(26),
  },
};
const Title3 = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 600,
  fontSize: pxToRem(24),
  lineHeight: pxToRem(32),
  "@media (max-width:900px)": {
    fontSize: pxToRem(20),
    lineHeight: pxToRem(26),
  },
  "@media (max-width:600px)": {
    fontSize: pxToRem(18),
    lineHeight: pxToRem(24),
  },
};
const Sub1 = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 600,
  fontSize: pxToRem(20),
  lineHeight: pxToRem(26),
};
const Sub2 = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 500,
  fontSize: pxToRem(18),
  lineHeight: pxToRem(24),
};
const Sub2S = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 600,
  fontSize: pxToRem(18),
  lineHeight: pxToRem(24),
};
const Body1 = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 400,
  fontSize: pxToRem(16),
  lineHeight: pxToRem(22),
};
const Body1S = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 500,
  fontSize: pxToRem(16),
  lineHeight: pxToRem(22),
};
const Body1SS = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 600,
  fontSize: pxToRem(16),
  lineHeight: pxToRem(22),
};
const Body2 = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 400,
  fontSize: pxToRem(14),
  lineHeight: pxToRem(20),
};
const Body2S = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 500,
  fontSize: pxToRem(14),
  lineHeight: pxToRem(20),
};
const Body2SS = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 600,
  fontSize: pxToRem(14),
  lineHeight: pxToRem(20),
};
const Label1 = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 400,
  fontSize: pxToRem(16),
  lineHeight: pxToRem(20),
};
const Label1S = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 500,
  fontSize: pxToRem(16),
  lineHeight: pxToRem(20),
};
const Label1SS = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 600,
  fontSize: pxToRem(16),
  lineHeight: pxToRem(20),
};
const Label2 = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 400,
  fontSize: pxToRem(14),
  lineHeight: pxToRem(20),
};
const Label2S = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 500,
  fontSize: pxToRem(14),
  lineHeight: pxToRem(20),
};
const Label2SS = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 600,
  fontSize: pxToRem(14),
  lineHeight: pxToRem(20),
};
const Label3 = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 400,
  fontSize: pxToRem(12),
  lineHeight: pxToRem(16),
};
const Label3S = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 500,
  fontSize: pxToRem(12),
  lineHeight: pxToRem(16),
};
const Label3SS = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 600,
  fontSize: pxToRem(12),
  lineHeight: pxToRem(16),
};
const Label4 = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 400,
  fontSize: pxToRem(10),
  lineHeight: pxToRem(14),
};
const Label4S = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 500,
  fontSize: pxToRem(10),
  lineHeight: pxToRem(14),
};
const Label5SS = {
  fontFamily: baseProperties.fontFamily,
  fontWeight: 600,
  fontSize: pxToRem(8),
  lineHeight: pxToRem(10),
};

// ─────────────────────────────────────────────────────────────
// TYPE DECLARATIONS
// ─────────────────────────────────────────────────────────────
declare module "@mui/material/Typography" {
  export interface TypographyPropsVariantOverrides {
    // ── Existing variants — kept for backward compatibility ──
    title1Main: true;
    title1Secondary: true;
    title1Tertiary: true;
    title1Disabled: true;
    title1LabelWhite: true;
    title1LabelBrand: true;
    title2Main: true;
    title3Main: true;
    subtitle1Main: true;
    subtitle1Secondary: true;
    subtitle2Main: true;
    subtitle2Tertiary: true;
    subtitle2Secondary: true;
    subtitle2StrongerMain: true;
    subtitle2StrongerSecondary: true;
    subtitle2StrongerWhite: true;
    body1Main: true;
    body1Secondary: true;
    body1Disabled: true;
    body1Tertiary: true;
    body1Brand: true;
    body1StrongMain: true;
    body1StrongBrand: true;
    body1StrongSecondary: true;
    body1StrongerSecondary: true;
    body1StrongerMain: true;
    body1StrongerBrand: true;
    body2Main: true;
    body2Secondary: true;
    body2Tertiary: true;
    body2Disabled: true;
    body2Brand: true;
    body2StrongMain: true;
    body2StrongTertiary: true;
    body2StrongMainPlus2: true;
    body2StrongBrand: true;
    body2StrongSecondary: true;
    body2StrongerMain: true;
    body2StrongerSecondary: true;
    body2StrongerTertiary: true;
    body2StrongerDisabled: true;
    label1Main: true;
    label1Tertiary: true;
    label1StrongerWhite: true;
    label1StrongMain: true;
    label1StrongBrand: true;
    label1StrongTertiary: true;
    label1StrongSecondary: true;
    label1StrongerMain: true;
    label1StrongerSecondary: true;
    label1StrongerDisabled: true;
    label2Main: true;
    label2White: true;
    label2Primary: true;
    label2Secondary: true;
    label2Tertiary: true;
    label2Disabled: true;
    label2Error: true;
    label2StrongSecondary: true;
    label2StrongTertiary: true;
    label2StrongDisabled: true;
    label2Brand: true;
    label2StrongMain: true;
    label2StrongBrand: true;
    label2StrongerMain: true;
    label2StrongerBrand: true;
    label2StrongerSecondary: true;
    label3Main: true;
    label3White: true;
    label3Brand: true;
    label3Success: true;
    label3Secondary: true;
    label3Tertiary: true;
    label3Disabled: true;
    label3Error: true;
    label3StrongSecondary: true;
    label3StrongTertiary: true;
    label3StrongDisabled: true;
    label3StrongMain: true;
    label3StrongBrand: true;
    label3StrongWhite: true;
    label3StrongError: true;
    label3StrongSuccess: true;
    label3StrongOrange: true;
    label3StrongerSecondary: true;
    label3StrongerTertiary: true;
    label3StrongerMain: true;
    label3MainWhite: true;
    label4Main: true;
    label4MainBrand: true;
    label4MainWhite: true;
    label4Disabled: true;
    label4Secondary: true;
    label4Tertiary: true;
    label4TertiaryPlus2: true;
    label4ErrorPrimary: true;
    label4StrongTertiary: true;
    label4StrongMain: true;
    label4StrongWhite: true;
    label4StrongBrand: true;
    label4StrongSecondary: true;
    Label4StrongMain: true;
    label5StrongerYellow: true;
  }
}

interface ExtendedTypographyOptions extends TypographyOptions {
  title1Main: React.CSSProperties;
  title1Secondary: React.CSSProperties;
  title1Tertiary: React.CSSProperties;
  title1Disabled: React.CSSProperties;
  title1LabelWhite: React.CSSProperties;
  title1LabelBrand: React.CSSProperties;
  title2Main: React.CSSProperties;
  title3Main: React.CSSProperties;
  subtitle1Main: React.CSSProperties;
  subtitle1Secondary: React.CSSProperties;
  subtitle2Main: React.CSSProperties;
  subtitle2Tertiary: React.CSSProperties;
  subtitle2Secondary: React.CSSProperties;
  subtitle2StrongerMain: React.CSSProperties;
  subtitle2StrongerSecondary: React.CSSProperties;
  subtitle2StrongerWhite: React.CSSProperties;
  body1Main: React.CSSProperties;
  body1Secondary: React.CSSProperties;
  body1Disabled: React.CSSProperties;
  body1Tertiary: React.CSSProperties;
  body1Brand: React.CSSProperties;
  body1StrongMain: React.CSSProperties;
  body1StrongBrand: React.CSSProperties;
  body1StrongSecondary: React.CSSProperties;
  body1StrongerSecondary: React.CSSProperties;
  body1StrongerMain: React.CSSProperties;
  body1StrongerBrand: React.CSSProperties;
  body2Main: React.CSSProperties;
  body2Secondary: React.CSSProperties;
  body2Tertiary: React.CSSProperties;
  body2Disabled: React.CSSProperties;
  body2Brand: React.CSSProperties;
  body2StrongMain: React.CSSProperties;
  body2StrongTertiary: React.CSSProperties;
  body2StrongMainPlus2: React.CSSProperties;
  body2StrongBrand: React.CSSProperties;
  body2StrongSecondary: React.CSSProperties;
  body2StrongerMain: React.CSSProperties;
  body2StrongerSecondary: React.CSSProperties;
  body2StrongerTertiary: React.CSSProperties;
  body2StrongerDisabled: React.CSSProperties;
  label1Main: React.CSSProperties;
  label1Tertiary: React.CSSProperties;
  label1StrongMain: React.CSSProperties;
  label1StrongerWhite: React.CSSProperties;
  label1StrongTertiary: React.CSSProperties;
  label1StrongBrand: React.CSSProperties;
  label1StrongSecondary: React.CSSProperties;
  label1StrongerMain: React.CSSProperties;
  label1StrongerSecondary: React.CSSProperties;
  label1StrongerDisabled: React.CSSProperties;
  label2Main: React.CSSProperties;
  label2White: React.CSSProperties;
  label2Secondary: React.CSSProperties;
  label2Primary: React.CSSProperties;
  label2Tertiary: React.CSSProperties;
  label2Disabled: React.CSSProperties;
  label2StrongSecondary: React.CSSProperties;
  label2StrongTertiary: React.CSSProperties;
  label2StrongDisabled: React.CSSProperties;
  label2Brand: React.CSSProperties;
  label2StrongMain: React.CSSProperties;
  label2Error: React.CSSProperties;
  label2StrongBrand: React.CSSProperties;
  label2StrongerMain: React.CSSProperties;
  label2StrongerBrand: React.CSSProperties;
  label2StrongerSecondary: React.CSSProperties;
  label3Main: React.CSSProperties;
  label3White: React.CSSProperties;
  label3Brand: React.CSSProperties;
  label3Success: React.CSSProperties;
  label3Secondary: React.CSSProperties;
  label3StrongSecondary: React.CSSProperties;
  label3StrongTertiary: React.CSSProperties;
  label3StrongDisabled: React.CSSProperties;
  label3StrongMain: React.CSSProperties;
  label3StrongBrand: React.CSSProperties;
  label3StrongWhite: React.CSSProperties;
  label3StrongerSecondary: React.CSSProperties;
  label3StrongerTertiary: React.CSSProperties;
  label3StrongerMain: React.CSSProperties;
  label3Tertiary: React.CSSProperties;
  label3Disabled: React.CSSProperties;
  label3Error: React.CSSProperties;
  label3StrongError: React.CSSProperties;
  label3StrongSuccess: React.CSSProperties;
  label3StrongOrange: React.CSSProperties;
  label3MainWhite: React.CSSProperties;
  label4MainBrand: React.CSSProperties;
  label4Main: React.CSSProperties;
  label4MainWhite: React.CSSProperties;
  label4ErrorPrimary: React.CSSProperties;
  label4StrongMain: React.CSSProperties;
  label4StrongWhite: React.CSSProperties;
  label4StrongBrand: React.CSSProperties;
  label4StrongSecondary: React.CSSProperties;
  label4Disabled: React.CSSProperties;
  label4Secondary: React.CSSProperties;
  label4Tertiary: React.CSSProperties;
  label4TertiaryPlus2: React.CSSProperties;
  label4StrongTertiary: React.CSSProperties;
  Label4StrongMain: React.CSSProperties;
  label5StrongerYellow: React.CSSProperties;
}

// ─────────────────────────────────────────────────────────────
// VARIANT DEFINITIONS
// Key change: colours now use CSS vars via text.* (hex in colors.ts)
// !important removed — use sx/className to override when needed
// ─────────────────────────────────────────────────────────────
const variant: ExtendedTypographyOptions = {
  // Titles
  title1Main: { ...Title1, color: text.main },
  title1Secondary: { ...Title1, color: text.secondary },
  title1Tertiary: { ...Title1, color: text.tertiary },
  title1Disabled: { ...Title1, color: text.disabled },
  title1LabelWhite: { ...Title1, color: text.labelWhite },
  title1LabelBrand: { ...Title1, color: text.labelBrand },
  title2Main: { ...Title2, color: text.main },
  title3Main: { ...Title3, color: text.main },

  // Subtitles
  subtitle1Main: { ...Sub1, color: text.main },
  subtitle1Secondary: { ...Sub1, color: text.secondary },
  subtitle2Main: { ...Sub2, color: text.main },
  subtitle2Tertiary: { ...Sub2, color: text.tertiary },
  subtitle2Secondary: { ...Sub2, color: text.secondary },
  subtitle2StrongerMain: { ...Sub2S, color: text.main },
  subtitle2StrongerSecondary: { ...Sub2S, color: text.secondary },
  subtitle2StrongerWhite: { ...Sub2S, color: text.labelWhite },

  // Body 1 (16px)
  body1Main: { ...Body1, color: text.main },
  body1Secondary: { ...Body1, color: text.secondary },
  body1Disabled: { ...Body1, color: text.disabled },
  body1Tertiary: { ...Body1, color: text.tertiary },
  body1Brand: { ...Body1, color: text.labelBrand },
  body1StrongMain: { ...Body1S, color: text.main },
  body1StrongBrand: { ...Body1S, color: text.labelBrand },
  body1StrongSecondary: { ...Body1S, color: text.secondary },
  body1StrongerSecondary: { ...Body1SS, color: text.secondary },
  body1StrongerMain: { ...Body1SS, color: text.main },
  body1StrongerBrand: { ...Body1SS, color: text.labelBrand },

  // Body 2 (14px)
  body2Main: { ...Body2, color: text.main },
  body2Secondary: { ...Body2, color: text.secondary },
  body2Tertiary: { ...Body2, color: text.tertiary },
  body2Disabled: { ...Body2, color: text.tertiary },
  body2Brand: { ...Body2, color: text.labelBrand, cursor: "pointer" },
  body2StrongMain: { ...Body2S, color: text.main },
  body2StrongTertiary: { ...Body2S, color: text.tertiary },
  body2StrongMainPlus2: { ...Body2S, color: text.main, fontSize: pxToRem(14) },
  body2StrongBrand: { ...Body2S, color: text.labelBrand },
  body2StrongSecondary: { ...Body2S, color: text.secondary },
  body2StrongerMain: { ...Body2SS, color: text.main },
  body2StrongerSecondary: { ...Body2SS, color: text.secondary },
  body2StrongerTertiary: { ...Body2SS, color: text.tertiary },
  body2StrongerDisabled: { ...Body2SS, color: text.disabled },

  // Label 1 (16px)
  label1Main: { ...Label1, color: text.main },
  label1Tertiary: { ...Label1, color: text.tertiary },
  label1StrongMain: { ...Label1S, color: text.main },
  label1StrongTertiary: { ...Label1S, color: text.tertiary },
  label1StrongBrand: { ...Label1S, color: text.labelBrand },
  label1StrongSecondary: { ...Label1S, color: text.secondary },
  label1StrongerMain: { ...Label1SS, color: text.main },
  label1StrongerWhite: { ...Label1SS, color: text.labelWhite },
  label1StrongerSecondary: { ...Label1SS, color: text.secondary },
  label1StrongerDisabled: { ...Label1SS, color: text.disabled },

  // Label 2 (14px)
  label2Main: { ...Label2, color: text.main },
  label2White: { ...Label2, color: text.labelWhite },
  label2Error: { ...Label2, color: text.error },
  label2Secondary: { ...Label2, color: text.secondary },
  label2Primary: { ...Label2, color: text.main },
  label2Tertiary: { ...Label2, color: text.tertiary },
  label2Disabled: { ...Label2, color: text.disabled },
  label2Brand: { ...Label2, color: text.labelBrand, cursor: "pointer" },
  label2StrongMain: { ...Label2S, color: text.main },
  label2StrongSecondary: { ...Label2S, color: text.secondary },
  label2StrongTertiary: { ...Label2S, color: text.tertiary },
  label2StrongDisabled: { ...Label2S, color: text.disabled },
  label2StrongBrand: { ...Label2S, color: text.labelBrand, cursor: "pointer" },
  label2StrongerMain: { ...Label2SS, color: text.main },
  label2StrongerBrand: { ...Label2SS, color: text.labelBrand },
  label2StrongerSecondary: { ...Label2SS, color: text.secondary },

  // Label 3 (12px)
  label3Main: { ...Label3, color: text.main },
  label3White: { ...Label3, color: text.labelWhite },
  label3Brand: { ...Label3, color: text.labelBrand },
  label3Success: { ...Label3, color: success.main },
  label3Secondary: { ...Label3, color: text.secondary },
  label3Tertiary: { ...Label3, color: text.tertiary },
  label3Disabled: { ...Label3, color: text.disabled },
  label3Error: { ...Label3, color: text.error },
  label3MainWhite: { ...Label3, color: text.labelWhite },
  label3StrongMain: { ...Label3S, color: text.main, cursor: "pointer" },
  label3StrongSecondary: { ...Label3S, color: text.secondary },
  label3StrongTertiary: { ...Label3S, color: text.tertiary },
  label3StrongDisabled: { ...Label3S, color: text.disabled },
  label3StrongBrand: { ...Label3S, color: text.labelBrand },
  label3StrongWhite: { ...Label3S, color: text.labelWhite },
  label3StrongError: { ...Label3S, color: text.error },
  label3StrongSuccess: { ...Label3S, color: success.main },
  label3StrongOrange: { ...Label3S, color: "#F97E1A" },
  label3StrongerMain: { ...Label3SS, color: text.main },
  label3StrongerSecondary: { ...Label3SS, color: text.secondary },
  label3StrongerTertiary: { ...Label3SS, color: text.tertiary },

  // Label 4 (10px)
  label4Main: { ...Label4, color: text.main },
  label4MainBrand: { ...Label4, color: text.labelBrand },
  label4MainWhite: { ...Label4, color: text.labelWhite },
  label4Disabled: { ...Label4, color: text.disabled },
  label4Secondary: { ...Label4, color: text.secondary },
  label4Tertiary: { ...Label4, color: text.tertiary },
  label4TertiaryPlus2: { ...Label4, color: text.tertiary },
  label4ErrorPrimary: { ...Label4, color: text.error },
  label4StrongMain: { ...Label4S, color: text.main },
  Label4StrongMain: { ...Label4S, color: text.main },
  label4StrongTertiary: { ...Label4S, color: text.tertiary },
  label4StrongWhite: { ...Label4S, color: text.labelWhite },
  label4StrongBrand: { ...Label4S, color: text.labelBrand },
  label4StrongSecondary: { ...Label4S, color: text.secondary },

  // Label 5 (8px)
  label5StrongerYellow: { ...Label5SS, color: warning.main },
};

export default variant;
